// Stacked area of weekly active users by build bucket, one point per day
// (hand-rolled SVG like components/analytics/Charts.tsx, no chart library).
// Days are placed by date, so a day without a stored rollup is bridged rather
// than drawn as a dip to zero. The last point is today's live count.

import type { AppVersionsAdoption } from "../../services/appVersions";
import { bucketColors, formatCount, formatShare, shortDay } from "./format";

const W = 760;
const H = 260;
const PAD = { top: 14, right: 12, bottom: 26, left: 46 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;
const DAY_MS = 86_400_000;

function dayIndex(day: string, first: string): number {
  return Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / DAY_MS);
}

// Top of the y-axis: four equal ticks on a round step.
function niceMax(value: number): number {
  const raw = Math.max(value, 4) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((s) => s * magnitude >= raw) ?? 10;
  return step * magnitude * 4;
}

export function AdoptionLegend({ data }: { data: AppVersionsAdoption }) {
  const last = data.points[data.points.length - 1];
  const colors = bucketColors(data.buckets);
  return (
    <div className="av-legend">
      {data.buckets.map((b, i) => (
        <span key={b.key}>
          <i style={{ background: colors[i] }} />
          {b.label} <b>{last ? formatShare(last.users[i] ?? 0, last.total) : "—"}</b>
        </span>
      ))}
    </div>
  );
}

export function AdoptionChart({ data }: { data: AppVersionsAdoption }) {
  const { points, buckets } = data;
  if (points.length < 2) {
    return (
      <div className="state-box">
        Daily history starts with the first nightly rollup (23:55 UTC). Today's live split is below.
      </div>
    );
  }
  const today = points[points.length - 1].day;
  const first = new Date(Date.parse(`${today}T00:00:00Z`) - (data.days - 1) * DAY_MS)
    .toISOString()
    .slice(0, 10);
  const span = Math.max(data.days - 1, 1);
  const xAt = (day: string) => PAD.left + (dayIndex(day, first) / span) * PLOT_W;
  const max = niceMax(Math.max(...points.map((p) => p.total), 1));
  const yAt = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H;
  const colors = bucketColors(buckets);

  // Cumulative tops per point, one band per bucket.
  const cumulative = points.map(() => 0);
  const bands = buckets.map((bucket, j) => {
    const lower = cumulative.slice();
    points.forEach((p, i) => (cumulative[i] += p.users[j] ?? 0));
    const top = points.map((p, i) => `${xAt(p.day).toFixed(1)},${yAt(cumulative[i]).toFixed(1)}`);
    const bottom = points
      .map((p, i) => `${xAt(p.day).toFixed(1)},${yAt(lower[i]).toFixed(1)}`)
      .reverse();
    return <path key={bucket.key} d={`M${top.join(" L")} L${bottom.join(" L")} Z`} fill={colors[j]} />;
  });

  const ticks = [0, 1, 2, 3, 4].map((k) => (max / 4) * k);
  const xLabels = [0, 0.25, 0.5, 0.75, 1].map((f) => {
    const day = new Date(Date.parse(`${first}T00:00:00Z`) + Math.round(f * span) * DAY_MS)
      .toISOString()
      .slice(0, 10);
    return { day, x: xAt(day) };
  });
  const slot = PLOT_W / span;

  return (
    <svg
      className="chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Stacked area of weekly active users by build over the last ${data.days} days`}
    >
      {ticks.map((v) => (
        <g key={v} className="chart-axis">
          <line className="chart-grid" x1={PAD.left} x2={PAD.left + PLOT_W} y1={yAt(v)} y2={yAt(v)} />
          <text x={PAD.left - 6} y={yAt(v) + 3} textAnchor="end">
            {formatCount(v)}
          </text>
        </g>
      ))}
      {bands}
      <line
        className="chart-base"
        x1={PAD.left} y1={PAD.top + PLOT_H} x2={PAD.left + PLOT_W} y2={PAD.top + PLOT_H}
      />
      <g className="chart-axis">
        {xLabels.map(({ day, x }, i) => (
          <text key={day} x={x} y={H - 8} textAnchor={i === 0 ? "start" : i === 4 ? "end" : "middle"}>
            {shortDay(day)}
          </text>
        ))}
      </g>
      {points.map((p) => (
        <rect
          key={p.day}
          className="av-hit"
          x={xAt(p.day) - slot / 2}
          y={PAD.top}
          width={slot}
          height={PLOT_H}
        >
          <title>
            {`${shortDay(p.day)}${p.live ? " (live)" : ""}: ${p.total.toLocaleString()} users\n` +
              buckets
                .map((b, i) => `${b.label}: ${(p.users[i] ?? 0).toLocaleString()}`)
                .reverse()
                .join("\n")}
          </title>
        </rect>
      ))}
    </svg>
  );
}
